<template>
  <div class="purchase-item-model-edit-form flex flex-col">
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <label>
        <span class="sr-only block text-sm">Artikelnaam</span>
        <InputText
          v-model="purchaseItem.name"
          ref="inputName"
          placeholder="Artikelnaam"
          :errors="v$.name.$errors"
          @focusout="v$.name.$touch()"
        />
      </label>

      <label>
        <span class="sr-only block text-sm">Eenheid</span>
        <UnitCombobox
          v-model="purchaseItem.unit"
          input-placeholder="Eenheid"
          grow
          :errors="v$.unit.$errors"
          @focusout="v$.unit.$touch()"
        />
      </label>

      <label>
        <span class="sr-only block text-sm">Leverancier</span>
        <KnownBusinessEntityCombobox
          v-model="purchaseItem.supplier"
          :items="knownBusinessEntities"
          input-placeholder="Leverancier"
          items-key-name="id"
          items-value-name="name"
          creation-option
          grow
          auto-select-newly-created-model
          :creation-model-type="KnownBusinessEntity"
          :errors="v$.supplier.$errors"
          @focusout="v$.supplier.$touch()"
        />
      </label>

      <ModelEditFormProjectPurchaseItem
        v-if="creationParameters?.project"
        :project="creationParameters.project"
        :purchase-item="purchaseItem"
        @input="anyPriceWasChanged = true"
        ref="modelEditFormProjectPurchaseItem"
      />

      <div class="flex items-end gap-x-2">
        <label class="grow">
          <span class="block text-sm">Eenheidsprijs</span>
          <InputNumber
            v-model="purchaseItem.unitPrice"
            placeholder="Eenheidsprijs"
            allow-zero
            @input="anyPriceWasChanged = true"
          >
            <span class="sm:text-sm">&euro;</span>
          </InputNumber>
        </label>

        <GeneralButton
          v-if="!_isCreating"
          color="secondary"
          :icon="['fas', 'gas-pump']"
          :disabled="surchargeIsLoading"
          @click="openSurchargeForm"
        >
          <span class="flex flex-col items-start">
            <span>Brandstoftoeslag</span>
            <span v-if="surchargeDetail" class="text-xs">{{ surchargeDetail }}</span>
          </span>
        </GeneralButton>
      </div>

      <ModelEditFormRetroactivePricing
        v-if="!_isCreating && anyPriceWasChanged"
        v-model="retroactiveDate"
        price-type="purchase"
      >
        <template #subtitle>
          <span class="block text-xs">
            Alle inkopen <b>zonder inkoopfactuurreferentie</b> vanaf deze datum worden aangepast
          </span>
        </template>
      </ModelEditFormRetroactivePricing>

      <label>
        <span class="sr-only block text-sm">Corresponderend verkoopartikel</span>
        <InputCombobox
          v-model="purchaseItem.saleItem"
          :items="saleItems"
          items-key-name="id"
          :items-value-function="(item: SaleItem) => `${item.name} ${item.getUnitName()}`"
          input-placeholder="Corresponderend verkoopartikel"
          creation-option
          grow
          :creation-model-type="SaleItem"
          @item-created="onSaveSaleItem"
          :creation-parameters="saleItemModelFormCreationParameters"
        />
      </label>
    </div>
    <FormValidationAlert v-model="v$" />
  </div>
</template>

<script setup lang="ts">
import { inject, onMounted, ref, useTemplateRef } from "vue"
import { storeToRefs } from "pinia"
import { PurchaseItem } from "@/models/purchase-item.model"
import KnownBusinessEntityCombobox from "@/components/known_business_entity/KnownBusinessEntityCombobox.vue"
import InputText from "@/components/inputs/InputText.vue"
import InputNumber from "@/components/inputs/InputNumber.vue"
import { useSaleItemStore } from "@/store/sale-items.store"
import { SaleItem } from "@/models/sale-item.model"
import { requiredValidator } from "@/helper/validation/validators"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import { PurchaseItemController } from "@/controllers/model-controllers/purchase-item-controller"
import { formFocusHack } from "@/helper/form-helper"
import { upperFirst } from "lodash-es"
import UnitCombobox from "@/components/inputs/UnitCombobox.vue"
import { Project } from "@/models/project.model"
import ModelEditFormProjectPurchaseItem from "@/components/administrative_item/ModelEditFormProjectPurchaseItem.vue"
import ModelEditFormRetroactivePricing from "@/components/administrative_item/ModelEditFormRetroactivePricing.vue"
import type PurchaseController from "@/controllers/model-controllers/purchase-controller"
import { Purchase } from "@/models/purchase.model"
import { uniquePurchaseItemValidator } from "@/helper/validation/purchase-item-validation.ts"
import FormValidationAlert from "@/components/validation/FormValidationAlert.vue"
import { useKnownBusinessEntityStore } from "@/store/known-business-entities.store.ts"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import usePurchaseSurchargePeriodRetrieval from "@/composables/administration/purchase-surcharge-period-retrieval"
import moment from "moment"

const inputName = ref()

const knownBusinessEntityStore = useKnownBusinessEntityStore()
const saleItemStore = useSaleItemStore()
const { knownBusinessEntities } = storeToRefs(knownBusinessEntityStore)
const { saleItems } = storeToRefs(saleItemStore)

const rules = {
  supplier: { requiredValidator },
  name: { requiredValidator },
  unit: { requiredValidator },
  $validator: uniquePurchaseItemValidator
}

const controller: PurchaseItemController = inject("purchaseItemController")!
const {
  model: purchaseItem,
  initialText,
  isCreating,
  v$
} = useFormConfigurator<PurchaseItem>({ controller, rules, customSaveHandler })

// Copy initial value. Else, after creation, the model form appearance will change to edit mode.
const _isCreating = isCreating.value

const creationParameters: { project?: Project; administrativeEvent?: Purchase } =
  inject("creationParameters")!
let saleItemModelFormCreationParameters = creationParameters
if (creationParameters.administrativeEvent) {
  // In case of editing a purchase item based on a purchase processing item, pass down project information of the
  // associated sale.
  saleItemModelFormCreationParameters = {
    project: creationParameters.administrativeEvent.associatedSale?.project
  }
}

onMounted(() => {
  if (initialText) {
    purchaseItem.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, purchaseItem, "name")
})

const purchaseController: PurchaseController = inject("purchaseController")!
const modelEditFormProjectPurchaseItem = useTemplateRef<typeof ModelEditFormProjectPurchaseItem>(
  "modelEditFormProjectPurchaseItem"
)
async function customSaveHandler(save: () => Promise<void>) {
  await save()
  if (creationParameters.project) {
    await modelEditFormProjectPurchaseItem.value?.savePriceAgreement()
  }
  if (retroactiveDate.value) {
    await purchaseController.applyPricesRetroactively(purchaseItem.value, retroactiveDate.value)
  }
}

function onSaveSaleItem(saleItem: SaleItem) {
  purchaseItem.value.saleItem = saleItem
}

const anyPriceWasChanged = ref(false)
const retroactiveDate = ref()

// Only available when editing an existing purchase item, since the surcharge period is linked to its id.
const {
  surchargeDetail,
  isLoading: surchargeIsLoading,
  openSurchargeForm
} = !_isCreating
  ? usePurchaseSurchargePeriodRetrieval(purchaseItem.value, moment())
  : { surchargeDetail: ref(null), isLoading: ref(false), openSurchargeForm: () => {} }
</script>

<style scoped></style>
